// src/features/permissions/PermissionsPage.tsx
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Card, EmptyState, Modal } from '../../app/components/foundation';
import { ControlPlaneDataSurface, ControlPlanePage, ControlPlaneSection } from '../../app/components/foundation/control-plane';
import { VaniaContentSkeleton, VaniaLoadingSystem } from '../../app/components/loading';
import { createRole } from '../roles/api/roles.api';
import { getSecurityRoles } from '../security/api/security.api';
import type { SecurityRole } from '../security/types/security.types';
import { PermissionsHeader } from './components';
import './styles/permissions.css';

type ScopeFilter = 'all' | 'system' | 'tenant';

export function PermissionsPage() {
  const [roles, setRoles] = useState<SecurityRole[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTenant, setSearchTenant] = useState('');
  const [searchRole, setSearchRole] = useState('');
  const [scope, setScope] = useState<ScopeFilter>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const initialLoading = loading && roles.length === 0;

  const loadRoles = () => {
    setLoading(true);
    setError(null);
    return getSecurityRoles()
      .then(setRoles)
      .catch(() => setError('خطا در دریافت نقش‌ها و مجوزها'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    void loadRoles();
  }, []);

  const filteredRoles = useMemo(() => {
    const tenantQuery = searchTenant.trim().toLocaleLowerCase();
    const roleQuery = searchRole.trim().toLocaleLowerCase();

    return roles.filter((role) => {
      const roleIsSystem = isSystemRole(role);
      const roleIsTenant = isTenantRole(role);
      const matchesScope = scope === 'all' || (scope === 'system' && roleIsSystem) || (scope === 'tenant' && roleIsTenant);
      const tenantText = `${role.tenant_name ?? ''} ${role.tenant_id ?? ''}`.toLocaleLowerCase();
      const matchesTenant = !tenantQuery || tenantText.includes(tenantQuery) || (roleIsSystem && 'global system سیستم سراسری'.includes(tenantQuery));
      const matchesRole = !roleQuery || `${role.name} ${role.slug}`.toLocaleLowerCase().includes(roleQuery);
      return matchesScope && matchesTenant && matchesRole;
    });
  }, [roles, scope, searchRole, searchTenant]);

  const tenants = useMemo(() => {
    const entries = new Map<string, { id: number; name: string }>();
    roles.filter(isTenantRole).forEach((role) => {
      if (role.tenant_id == null || entries.has(String(role.tenant_id))) return;
      entries.set(String(role.tenant_id), { id: role.tenant_id, name: role.tenant_name || String(role.tenant_id) });
    });
    return Array.from(entries.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [roles]);

  return (
    <ControlPlanePage className="vania-permissions-control-plane-page" title="">
      <ControlPlaneSection>
        <ControlPlaneDataSurface>
          <PermissionsHeader onCreateRole={() => setCreateOpen(true)} />
          <PermissionFilters
            searchTenant={searchTenant}
            searchRole={searchRole}
            scope={scope}
            onSearchTenant={setSearchTenant}
            onSearchRole={setSearchRole}
            onScope={setScope}
          />
          {error && !loading ? <p className="vania-async-state vania-async-state-error" role="alert">{error}</p> : null}
          {initialLoading ? <PermissionsTableSkeleton /> : null}
          {!loading && !error && roles.length === 0 ? <EmptyState message="نقشی برای نمایش وجود ندارد." /> : null}
          {roles.length > 0 ? <ScopedPermissionSections roles={filteredRoles} /> : null}
        </ControlPlaneDataSurface>
      </ControlPlaneSection>
      <CreateRoleModal open={createOpen} tenants={tenants} onClose={() => setCreateOpen(false)} onCreated={loadRoles} />
    </ControlPlanePage>
  );
}

function isSystemRole(role: SecurityRole): boolean {
  return role.scope === 'system' && (role.is_system === true || role.is_system === 1);
}

function isTenantRole(role: SecurityRole): boolean {
  return role.scope === 'tenant' && role.tenant_id != null && !(role.is_system === true || role.is_system === 1);
}

function ScopedPermissionSections({ roles }: { roles: SecurityRole[] }) {
  const systemRoles = roles.filter(isSystemRole);
  const tenantGroups = new Map<string, { label: string; id: number; roles: SecurityRole[] }>();

  roles.filter(isTenantRole).forEach((role) => {
    if (role.tenant_id == null) return;
    const key = String(role.tenant_id);
    const group = tenantGroups.get(key) ?? { id: role.tenant_id, label: role.tenant_name || key, roles: [] };
    group.roles.push(role);
    tenantGroups.set(key, group);
  });

  const hasResults = systemRoles.length > 0 || tenantGroups.size > 0;
  return (
    <div className="vania-permissions-scoped-sections">
      {!hasResults ? <p className="vania-permissions-scope-unavailable" role="status">نتیجه‌ای برای فیلترهای انتخاب‌شده پیدا نشد.</p> : null}
      {systemRoles.length > 0 ? <PermissionScopeSection title="نقش‌های سیستمی" label="Global platform" roles={systemRoles} defaultOpen /> : null}
      {Array.from(tenantGroups.values()).map((group) => (
        <PermissionScopeSection key={group.id} title={`Tenant: ${group.label}`} label={`ID: ${group.id}`} roles={group.roles} defaultOpen />
      ))}
    </div>
  );
}

function PermissionScopeSection({ title, label, roles, defaultOpen = false }: { title: string; label: string; roles: SecurityRole[]; defaultOpen?: boolean }) {
  return (
    <details className="vania-permissions-scope-section" open={defaultOpen}>
      <summary className="vania-permissions-scope-heading">
        <span className="vania-permissions-scope-heading-title"><strong>{title}</strong><small>{label}</small></span>
        <span className="vania-permissions-scope-heading-count">{roles.length} role{roles.length === 1 ? '' : 's'}</span>
      </summary>
      <div className="vania-permissions-role-table-wrap">
        <table className="vania-permissions-role-table">
          <thead><tr><th>Role</th><th>Scope</th><th>Permissions</th><th>Actions</th></tr></thead>
          <tbody>{roles.map((role) => <PermissionRoleRow key={role.id} role={role} />)}</tbody>
        </table>
      </div>
    </details>
  );
}

function PermissionRoleRow({ role }: { role: SecurityRole }) {
  const [expanded, setExpanded] = useState(false);
  const system = isSystemRole(role);
  return (
    <>
      <tr className={`vania-permissions-role-table-row ${system ? 'is-system' : 'is-tenant'}`}>
        <td><strong>{role.name}</strong><span className="vania-permissions-role-slug" dir="ltr">{role.slug}</span></td>
        <td><span className={`vania-permission-scope-badge ${system ? 'is-system' : 'is-tenant'}`}>{system ? 'SYSTEM' : 'TENANT'}</span></td>
        <td>{role.permission_details.length} permissions</td>
        <td><button type="button" className="vania-permissions-expand-button" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded}>{expanded ? '▲ Hide permissions' : '▼ View permissions'}</button></td>
      </tr>
      {expanded ? <tr className="vania-permissions-expanded-row"><td colSpan={4}><ul className="vania-permissions-expanded-list">{role.permission_details.map((permission) => <li key={`${permission.scope}:${permission.id}`}><code>{permission.key}</code><span className={`vania-permission-scope-badge ${permission.scope === 'system' ? 'is-system' : 'is-tenant'}`}>{permission.scope === 'system' ? 'SYSTEM' : 'TENANT'}</span><span dir="ltr">{permission.source ?? (permission.scope === 'system' ? 'system_role_permissions' : 'role_permissions')}</span></li>)}</ul></td></tr> : null}
    </>
  );
}

function PermissionFilters({ searchTenant, searchRole, scope, onSearchTenant, onSearchRole, onScope }: { searchTenant: string; searchRole: string; scope: ScopeFilter; onSearchTenant: (value: string) => void; onSearchRole: (value: string) => void; onScope: (value: ScopeFilter) => void }) {
  return (
    <div className="vania-permissions-filters" role="search">
      <label>جستجوی Tenant<input value={searchTenant} onChange={(event) => onSearchTenant(event.target.value)} placeholder="نام یا شناسه Tenant" /></label>
      <label>جستجوی نقش<input value={searchRole} onChange={(event) => onSearchRole(event.target.value)} placeholder="نام یا slug نقش" /></label>
      <label>Scope<select value={scope} onChange={(event) => onScope(event.target.value as ScopeFilter)}><option value="all">همه</option><option value="system">SYSTEM</option><option value="tenant">TENANT</option></select></label>
    </div>
  );
}

function CreateRoleModal({ open, tenants, onClose, onCreated }: { open: boolean; tenants: Array<{ id: number; name: string }>; onClose: () => void; onCreated: () => Promise<unknown> | void }) {
  const [tenantId, setTenantId] = useState('');
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (open && !tenantId && tenants[0]) setTenantId(String(tenants[0].id)); }, [open, tenantId, tenants]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!tenantId || !name.trim() || !slug.trim()) { setError('Tenant، نام و slug الزامی است.'); return; }
    setSubmitting(true); setError(null);
    try {
      const payload = { name: name.trim(), slug: slug.trim() };
      const trimmedDescription = description.trim();
      await createRole(tenantId, trimmedDescription ? { ...payload, description: trimmedDescription } : payload);
      await onCreated();
      setName(''); setSlug(''); setDescription(''); onClose();
    } catch { setError('ایجاد نقش انجام نشد. دسترسی و اطلاعات واردشده را بررسی کنید.'); } finally { setSubmitting(false); }
  };

  return <Modal open={open} onClose={onClose} title="افزودن نقش" footer={<><button type="button" className="vania-permissions-modal-secondary" onClick={onClose}>انصراف</button><button form="create-role-form" type="submit" className="vania-permissions-modal-primary" disabled={submitting || tenants.length === 0}>{submitting ? 'در حال ثبت...' : 'ثبت نقش'}</button></>}>
    <form id="create-role-form" className="vania-permissions-create-form" onSubmit={submit}>
      <p className="vania-permissions-modal-note">این فرم از endpoint نقش‌های Tenant استفاده می‌کند؛ ایجاد نقش سیستمی در این API پشتیبانی نمی‌شود.</p>
      <label>Tenant<select value={tenantId} onChange={(event) => setTenantId(event.target.value)} required><option value="">انتخاب Tenant</option>{tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name} ({tenant.id})</option>)}</select></label>
      <label>نام نقش<input value={name} onChange={(event) => setName(event.target.value)} required /></label>
      <label>Slug<input value={slug} onChange={(event) => setSlug(event.target.value)} dir="ltr" required /></label>
      <label>توضیحات<textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={3} /></label>
      {!tenants.length ? <p className="vania-async-state vania-async-state-error">Tenant معتبری برای ایجاد نقش موجود نیست.</p> : null}
      {error ? <p className="vania-async-state vania-async-state-error" role="alert">{error}</p> : null}
    </form>
  </Modal>;
}

function PermissionsTableSkeleton() {
  return <VaniaLoadingSystem mode="content" label="در حال بارگذاری نقش‌ها و مجوزها"><Card noPadding aria-hidden="true"><div className="vania-permissions-events-table-region"><div className="vania-permissions-events-table-scroll"><table className="vania-permissions-events-table"><thead><tr>{['نقش', 'تننت', 'مجوزها', 'سیستمی'].map((heading) => <th key={heading}>{heading}</th>)}</tr></thead><tbody>{Array.from({ length: 8 }, (_, rowIndex) => <tr key={`permissions-skeleton-${rowIndex}`}>{Array.from({ length: 4 }, (_, cellIndex) => <td key={`permissions-skeleton-${rowIndex}-${cellIndex}`}><VaniaContentSkeleton style={{ width: cellIndex === 0 ? '70%' : '55%' }} /></td>)}</tr>)}</tbody></table></div></div></Card></VaniaLoadingSystem>;
}
